import React, { useContext, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Divider, Stack, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { format, parseISO } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import GoBackButton from 'components/shared/buttons/GoBackButton';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import { dateFormat } from 'common/constants/date';
import StyledNewsBodyContainer from 'common/theme/news-view/StyledNewsBodyContainer';
import StyledNewsDate from 'common/theme/news-view/StyledNewsDate';
import StyledNewsImage from 'common/theme/news-view/StyledNewsImage';
import ContentRenderer from 'components/shared/content/ContentRenderer';
import AppBarContext from 'context/AppBarContext';
import newsSlice from 'store/modules/news';

function NewsView() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['news', 'routes']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const pageTitle = t('news.view', { ns: 'routes' });
  const { id } = useParams();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const news = useSelector((state) => state.news.data.currentNews);
  const status = useSelector((state) => state.news.status.general);

  // Una sola imagen destacada; el contenedor + object-fit la adaptan a mobile/desktop.
  // (Fallback a la imagen mobile legada por compatibilidad con noticias antiguas.)
  const newsImage = useMemo(
    () => news?.featured_image || news?.mobile_featured_image || null,
    [news]
  );

  // Tiempo de lectura: ~200 palabras/min sobre el texto plano del cuerpo.
  const readingMinutes = useMemo(() => {
    const text = (news?.body || '').replace(/<[^>]+>/g, ' ');
    const words = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  }, [news?.body]);

  useEffect(() => {
    dispatch(newsSlice.actions.find(id));
  }, []);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle('');
  }, [setAppBarButton, setAppBarTitle]);

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <GoBackButton />
      <LoadingBlock
        emptyBlockProps={{ message: t('empty', { ns: 'news' }) }}
        isValid={news !== null}
        status={status}
        type="news">
        {news && (
          // Artículo editorial premium (ref. MySchoolUAE): card con hero ARRIBA,
          // luego fecha + tiempo de lectura, titular grande, resumen, divisor y cuerpo.
          <Box
            component="article"
            sx={{
              maxWidth: 820,
              mx: 'auto',
              width: '100%',
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.content',
              overflow: 'hidden',
              boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
            }}>
            {newsImage && (
              <Box sx={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden' }}>
                <StyledNewsImage src={newsImage} alt={news.title} />
              </Box>
            )}
            <Box sx={{ px: { xs: 2.5, sm: 5 }, py: { xs: 2.5, sm: 4 } }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                divider={<Box component="span" sx={{ color: 'text.disabled' }}>·</Box>}
                sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: 'gold.main' }} />
                  <StyledNewsDate color="secondary" variant="body2" sx={{ m: 0 }}>
                    {format(parseISO(news.created_at), dateFormat, { locale: dateLocale })}
                  </StyledNewsDate>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('readingTime', {
                    ns: 'news',
                    count: readingMinutes,
                    defaultValue: '{{count}} min de lectura',
                  })}
                </Typography>
              </Stack>
              <Typography
                component="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15,
                  fontSize: { xs: '1.7rem', sm: '2.1rem', md: '2.4rem' },
                  mb: 1.25,
                }}>
                {news.title}
              </Typography>
              {news.summary && (
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.6, mb: 2 }}>
                  {news.summary}
                </Typography>
              )}
              <Divider sx={{ borderColor: 'hairline.main', mb: 1 }} />
              <StyledNewsBodyContainer>
                <ContentRenderer value={news.body} />
              </StyledNewsBodyContainer>
            </Box>
          </Box>
        )}
      </LoadingBlock>
    </>
  );
}

export default NewsView;
